import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const TaxImplicator = ({ actionDescription, actionAmount, userId, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const analyzeTax = async () => {
    if (!actionDescription) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/learning/analyze-tax-implications', {
        action_description: actionDescription,
        action_amount: actionAmount || null,
        user_id: userId,
      });

      if (response.data.success) {
        setAnalysis(response.data.analysis);
      } else {
        setError(response.data.error || 'Failed to analyze tax implications');
      }
    } catch (err) {
      setError('Failed to analyze tax implications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (actionDescription && !analysis && !loading) analyzeTax();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionDescription]);

  if (!actionDescription) return null;

  return (
    <section className="aura-card border-t-2 border-t-pine light-surface p-6 mb-8 max-w-prose mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-pine" />
          <h3 className="font-display text-h2 text-primary">Tax implications</h3>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="icon-btn text-faint" aria-label="Close">
            ×
          </button>
        )}
      </div>

      {loading && (
        <p className="text-label text-secondary py-4 text-center">Analyzing tax implications…</p>
      )}

      {error && (
        <div className="surface-panel p-4 border border-danger/40 mb-4">
          <div className="flex items-center gap-2 text-danger">
            <ExclamationTriangleIcon className="w-5 h-5" />
            <p className="text-body">{error}</p>
          </div>
        </div>
      )}

      {analysis && !loading && (
        <div className="space-y-5 hairline-list">
          <div className="py-4 first:pt-0">
            <h4 className="text-label font-medium text-primary mb-2">Tax impact</h4>
            <p className="text-body text-secondary">{analysis.tax_impact}</p>
            {analysis.tax_amount_estimate > 0 && (
              <p className="figure text-body text-primary tabular-nums mt-2">
                Estimated tax: ${analysis.tax_amount_estimate.toLocaleString()}
              </p>
            )}
          </div>

          <div className="py-4">
            <h4 className="text-label font-medium text-primary mb-2">Net worth impact</h4>
            <p className="text-body text-secondary">
              This action will {analysis.net_worth_impact >= 0 ? 'increase' : 'decrease'} your net
              worth by approximately{' '}
              <span className="figure text-primary tabular-nums">
                ${Math.abs(analysis.net_worth_impact).toLocaleString()}
              </span>
            </p>
          </div>

          {analysis.estimated_loss > 0 && (
            <div className="py-4">
              <h4 className="text-label font-medium text-danger mb-2">Potential loss</h4>
              <p className="text-body text-secondary">
                Including taxes, expect to lose approximately{' '}
                <span className="figure text-danger tabular-nums">
                  ${analysis.estimated_loss.toLocaleString()}
                </span>
              </p>
            </div>
          )}

          {analysis.recommendations?.length > 0 && (
            <div className="py-4">
              <h4 className="text-label font-medium text-primary mb-2">Tax-efficient alternatives</h4>
              <ul className="list-disc pl-5 space-y-2">
                {analysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-body text-secondary">
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.government_guidance && (
            <div className="py-4">
              <h4 className="text-label font-medium text-primary mb-2">Government guidance</h4>
              <p className="text-body text-secondary">{analysis.government_guidance}</p>
            </div>
          )}

          {analysis.government_sources?.length > 0 && (
            <div className="py-4">
              <h4 className="text-label font-medium text-primary mb-2">Official sources</h4>
              <div className="space-y-2">
                {analysis.government_sources.map((source, idx) => (
                  <div key={idx} className="surface-panel p-3">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body text-pine underline"
                    >
                      {source.agency}: {source.title}
                    </a>
                    {source.relevance && (
                      <p className="text-label text-secondary mt-1">{source.relevance}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-label text-faint pt-4 border-t border-hairline">
            For educational purposes only. Consult a tax professional for personalized advice.
          </p>
        </div>
      )}

      {!analysis && !loading && !error && (
        <button type="button" onClick={analyzeTax} className="btn-secondary w-full">
          Analyze tax implications
        </button>
      )}
    </section>
  );
};

export default TaxImplicator;
